import { cn } from '@/lib/cn';
import type { ThemeTokenFamily } from '@personalizehq/editor';
import type { Theme } from '@personalizehq/schemas/ir';
import { ExternalLink, PanelLeftOpen } from 'lucide-react';
import { createContext, useContext } from 'react';

export type { ThemeTokenFamily } from '@personalizehq/editor';

const ThemeTokenNavigationContext = createContext<((family: ThemeTokenFamily, token: string) => void) | null>(null);

export function ThemeTokenNavigationProvider({
  onOpen,
  children,
}: {
  onOpen: (family: ThemeTokenFamily, token: string) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return <ThemeTokenNavigationContext value={onOpen}>{children}</ThemeTokenNavigationContext>;
}

export function themeTokenAnchor(family: ThemeTokenFamily, token: string): string {
  return `theme-token-${family}-${encodeURIComponent(token)}`;
}

export function themeTokenHref(family: ThemeTokenFamily, token: string): string {
  const search = new URLSearchParams({ family, token });
  return `/theme?${search.toString()}`;
}

export function ThemeTokenSample({
  family,
  token,
  theme,
  compact = false,
}: {
  family: ThemeTokenFamily;
  token: string;
  theme: Theme;
  compact?: boolean;
}): React.JSX.Element {
  if (family === 'colors') {
    const pair = theme.colors[token];
    if (!pair) return <span className="size-4 shrink-0 rounded-full border border-dashed" />;
    return (
      <span aria-label={`${token}: light ${pair.light}, dark ${pair.dark}`} className="flex size-4 shrink-0 overflow-hidden rounded-full border">
        <span className="w-1/2" style={{ background: pair.light }} />
        <span className="w-1/2" style={{ background: pair.dark }} />
      </span>
    );
  }

  if (family === 'fonts') {
    const font = theme.fonts[token];
    return (
      <span
        aria-label={font ? `${token}: ${font.family}, ${font.size}px, weight ${font.weight}` : `${token}: missing font token`}
        className={cn('shrink-0 truncate text-center', compact ? 'w-6 text-[10px]' : 'w-8 text-[12px]')}
        style={font ? { fontFamily: font.family, fontSize: compact ? 10 : 12, fontWeight: font.weight } : undefined}
      >
        Aa
      </span>
    );
  }

  if (family === 'radii') {
    const r = theme.radii[token];
    if (r === undefined) return <span className="size-4 shrink-0 rounded border border-dashed" />;
    const cap = (v: number) => Math.min(v, 8);
    const css = typeof r === 'number' ? cap(r) : `${cap(r.tl)}px ${cap(r.tr)}px ${cap(r.br)}px ${cap(r.bl)}px`;
    return <span aria-label={`${token} radius`} className="size-4 shrink-0 border bg-muted" style={{ borderRadius: css }} />;
  }

  if (family === 'elevations') {
    const e = theme.elevations[token];
    const c = e ? theme.colors[e.color]?.light : undefined;
    return (
      <span
        aria-label={e ? `${token} elevation` : `${token}: missing elevation token`}
        className={cn('size-4 shrink-0 rounded-sm border bg-background', !e && 'border-dashed')}
        style={e ? { boxShadow: `0 1px 3px ${c ?? 'rgba(0,0,0,0.35)'}` } : undefined}
      />
    );
  }

  if (family === 'gradients') {
    const g = theme.gradients[token];
    if (!g) return <span className="size-4 shrink-0 rounded-full border border-dashed" />;
    const stops = g.stops.map((s) => theme.colors[s]?.light ?? 'transparent').join(', ');
    return <span aria-label={`${token} gradient`} className="size-4 shrink-0 rounded-full border" style={{ backgroundImage: `linear-gradient(${g.angle}deg, ${stops})` }} />;
  }

  const variant = theme.buttons[token];
  // A variant COMPOSES foundation tokens (ADR 0014): bg may be a colour token, a gradient token, or
  // null; radius may be a literal, four corners, or a `radii` token. Resolve to a small swatch.
  const bgToken = typeof variant?.bg === 'string' ? variant.bg : undefined;
  const bg = bgToken ? theme.colors[bgToken] : undefined;
  const fg = variant ? theme.colors[variant.fg] : undefined;
  const rawRadius = variant?.radius;
  const radiusNum =
    typeof rawRadius === 'number'
      ? rawRadius
      : typeof rawRadius === 'string'
        ? (typeof theme.radii[rawRadius] === 'number' ? (theme.radii[rawRadius] as number) : 6)
        : rawRadius
          ? rawRadius.tl
          : 4;
  return (
    <span
      aria-label={variant ? `${token} button variant` : `${token}: missing button token`}
      className={cn('flex shrink-0 overflow-hidden border text-center font-medium', compact ? 'h-4 w-6 text-[8px]' : 'h-5 w-9 text-[9px]')}
      style={{
        borderRadius: Math.min(radiusNum, 8),
        borderStyle: variant ? 'solid' : 'dashed',
      }}
    >
      <span className="grid w-1/2 place-items-center" style={{ background: bg?.light, color: fg?.light }}>
        A
      </span>
      <span className="grid w-1/2 place-items-center" style={{ background: bg?.dark, color: fg?.dark }}>
        a
      </span>
    </span>
  );
}

export function OpenThemeTokenLink({
  family,
  token,
  className,
}: {
  family: ThemeTokenFamily;
  token: string | null | undefined;
  className?: string;
}): React.JSX.Element | null {
  const openIntegrated = useContext(ThemeTokenNavigationContext);
  if (!token) return null;
  const shared = {
    'aria-label': `Open ${token} in Theme`,
    title: `Open “${token}” in Theme`,
    className: cn(
      'inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      className
    ),
  };
  if (openIntegrated) {
    return (
      <button type="button" {...shared} onClick={() => openIntegrated(family, token)}>
        <PanelLeftOpen className="size-3.5" />
      </button>
    );
  }
  return (
    <a
      href={themeTokenHref(family, token)}
      target="_blank"
      rel="noreferrer"
      {...shared}
    >
      <ExternalLink className="size-3.5" />
    </a>
  );
}
